import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import { GridLoader } from "react-spinners";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { buscarLancamentosContaApuracao } from "../../../services/apuracao.service";
import { exibirCodRelatorio, formatarCnpj } from "../utils";
import { labelConta } from "../Detalhe/utils/agregarPorConta.util";
import FiltrosDinamicosLancamentos from "../components/FiltrosDinamicosLancamentos";
import TabelaLancamentos from "./components/TabelaLancamentos";
import styles from "../Detalhe/detalhe.styles.module.css";

export default function ApuracaoDetalheConta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const ecd = String(searchParams.get("ecd") || "").trim();
  // TXM-558 — modo consulta dos itens utilizados de um projeto aprovado.
  const projetoParam = Number.parseInt(searchParams.get("projeto") ?? "", 10);
  const projeto = Number.isInteger(projetoParam) && projetoParam > 0 ? projetoParam : null;

  const filtrosUiPai = useMemo(() => {
    const f = location.state?.filtrosUi;
    return f && typeof f === "object" ? f : {};
  }, [location.state]);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [dados, setDados] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(10);
  const [filtrosDinamicos, setFiltrosDinamicos] = useState([]);
  const [filtrosDinamicosQuery, setFiltrosDinamicosQuery] = useState([]);

  const filtrosDinamicosAtivos = useMemo(
    () =>
      filtrosDinamicos.filter((f) => {
        const v1 = String(f.valor ?? "").trim();
        const v2 = String(f.valor2 ?? "").trim();
        return Boolean(v1 || v2);
      }),
    [filtrosDinamicos]
  );

  // Aplica o filtro dinâmico pendente e reseta a página na mesma atualização, para que
  // `carregar()` (que depende de ambos) dispare uma única vez.
  const handleFiltrar = () => {
    setFiltrosDinamicosQuery(filtrosDinamicosAtivos);
    setPagina(1);
  };

  const carregar = useCallback(async () => {
    if (!ecd) {
      setLoading(false);
      setErro("Conta contábil não informada.");
      return;
    }

    setLoading(true);
    setErro("");

    const res = await buscarLancamentosContaApuracao(id, {
      ecdContaContabil: ecd,
      pagina,
      limite,
      filtrosUi: filtrosUiPai,
      filtrosDinamicos: filtrosDinamicosQuery,
      ...(projeto ? { projeto } : {}),
    });

    setLoading(false);

    if (!res.sucesso) {
      setErro(res.mensagem || "Não foi possível carregar os lançamentos.");
      if (res.status === 403) {
        navigate("/relatorios/apuracoes", { replace: true });
      }
      return;
    }

    setDados(res);
  }, [
    ecd,
    filtrosDinamicosQuery,
    filtrosUiPai,
    id,
    limite,
    navigate,
    pagina,
    projeto,
  ]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleFiltrosChange = (next) => {
    setFiltrosDinamicos(next);
  };

  const ap = dados?.apuracao || {};
  const conta = dados?.conta || { ecdContaContabil: ecd };
  const tipoLabel = exibirCodRelatorio(ap.tipo);

  if (!ecd && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Informe a conta na URL (parâmetro <code>ecd</code>) ou acesse a partir de Detalhes na
          tela de contas.
        </Alert>
        <button
          type="button"
          className={styles.linkVoltar}
          style={{ marginTop: 16 }}
          onClick={() => navigate(`/relatorios/apuracoes/${id}/detalhe`)}
        >
          ← Voltar aos detalhes da apuração
        </button>
      </Box>
    );
  }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.container}>
        <button
          type="button"
          className={styles.linkVoltar}
          onClick={() =>
            navigate(`/relatorios/apuracoes/${id}/detalhe${projeto ? `?projeto=${projeto}` : ""}`, {
              state: { filtrosUi: filtrosUiPai, apuracaoId: id },
            })
          }
        >
          ← Voltar aos detalhes da apuração
        </button>

        <header className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Lançamentos da conta contábil</h1>
            <p className={styles.pageSubtitle}>
              <span>
                {ap.empresa || "—"} · CNPJ {formatarCnpj(ap.cnpj)} · Apuração #{id} · {tipoLabel}
              </span>
              <span className={styles.contaRef}>
                {labelConta(conta.ecdContaContabil, conta.contaContabil)}
              </span>
              {/* Somente leitura: o grão da base legal é a conta, então ela se edita na
                  tela de contas, não aqui no drill-down de lançamentos. */}
              <span className={styles.contaRef}>
                Base legal: {conta.baseLegalDescricao || "não informada"}
              </span>
            </p>
          </div>
        </header>

        {erro && !dados ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        ) : (
          <>
            {dados?.colunas?.length > 0 && (
              <>
                <FiltrosDinamicosLancamentos
                  colunas={dados.colunas}
                  filtros={filtrosDinamicos}
                  onChange={handleFiltrosChange}
                />
                <div className={styles.lancFiltrosAcoes}>
                  <button type="button" className={styles.btnMini} onClick={handleFiltrar}>
                    Filtrar
                  </button>
                </div>
              </>
            )}

            {loading && !dados ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <GridLoader color="#18316e" />
              </Box>
            ) : (
              <TabelaLancamentos
                colunas={dados?.colunas || []}
                linhas={dados?.linhas || []}
                paginacao={dados?.paginacao || { pagina, limite, total: 0, totalPaginas: 0 }}
                loading={loading}
                onPageChange={setPagina}
                onPageSizeChange={(n) => {
                  setLimite(n);
                  setPagina(1);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
